import { HtmlStepperFormAgentSession } from '@superfunnel/types';
import { doc, setDoc } from 'firebase/firestore';
import { useCallback } from 'react';
import { db } from '../Firebase';
import { AgentChange } from '../types/agentChanges';
import { useBaseAgentSession } from './useBaseAgentSession';

interface AgentContext {
  formId: string;
}

export interface UseHtmlStepperAgentSessionReturn {
  agentChanges: AgentChange[];
  agentHasUnreviewedChanges: boolean;
  agentUpdateChanges: (
    changeUpdates: {
      id: string;
      status: 'accepted' | 'rejected';
    }[],
  ) => Promise<void>;
  agentUpdateFormData: (formData: unknown) => Promise<void>;
  agentSessionData: HtmlStepperFormAgentSession | null;
}

/**
 * Hook to listen to HTML Stepper Form agent session changes in Firestore
 * Converts agent-specific change format to generic AgentChange format
 * Gets threadId from AgentChatContext
 * @param {AgentContext | null} context - Agent context containing formId
 * @return {UseHtmlStepperAgentSessionReturn} Object with changes, session data, and change management functions
 */
export function useHtmlStepperAgentSession(
  context: AgentContext | null,
): UseHtmlStepperAgentSessionReturn {
  const baseSession = useBaseAgentSession<HtmlStepperFormAgentSession>({
    // Only enable when we have the required context
    enabled: !!context?.formId,

    // Title generation for step/finalStep/wrapper changes
    getTitleForChange: (change, sessionData) => {
      const { entity, entityId, type } = change;

      const getEntityName = (): string => {
        if (entity === 'wrapper') {
          return 'Wrapper';
        }

        if (entity === 'step') {
          const step = sessionData?.formData?.steps?.find(
            (s) => s.id === entityId,
          );
          if (step?.name) {
            return `"${step.name}"`;
          }
          const stepIndex = sessionData?.formData?.steps?.findIndex(
            (s) => s.id === entityId,
          );
          if (stepIndex !== undefined && stepIndex !== -1) {
            return `Step ${stepIndex + 1}`;
          }
          return 'Step';
        }

        if (entity === 'finalStep') {
          const finalStep = sessionData?.formData?.finalSteps?.find(
            (s) => s.id === entityId,
          );
          if (finalStep?.name) {
            return `"${finalStep.name}"`;
          }
          const finalStepIndex = sessionData?.formData?.finalSteps?.findIndex(
            (s) => s.id === entityId,
          );
          if (finalStepIndex !== undefined && finalStepIndex !== -1) {
            return `Final Step ${finalStepIndex + 1}`;
          }
          return 'Final Step';
        }

        return entityId;
      };

      const entityName = getEntityName();

      switch (type) {
        case 'created':
          return `Created ${entityName}`;
        case 'modified':
          return `Modified ${entityName}`;
        case 'deleted':
          return `Deleted ${entityName}`;
        default:
          return `Change in ${entityName}`;
      }
    },

    // Sort changes: wrapper first, then steps in order, then final steps in order
    sortChanges: (changes, sessionData) => {
      const stepsOrderMap = sessionData?.formData?.steps
        ? new Map<string, number>(
            sessionData.formData.steps.map((s, index) => [s.id, index]),
          )
        : null;

      const finalStepsOrderMap = sessionData?.formData?.finalSteps
        ? new Map<string, number>(
            sessionData.formData.finalSteps.map((s, index) => [s.id, index]),
          )
        : null;

      return [...changes].sort((a, b) => {
        // Wrapper always comes first
        if (a.entity === 'wrapper') return -1;
        if (b.entity === 'wrapper') return 1;

        // Steps come before final steps
        if (a.entity === 'step' && b.entity === 'finalStep') return -1;
        if (a.entity === 'finalStep' && b.entity === 'step') return 1;

        // Sort within same entity type
        if (a.entity === 'step' && b.entity === 'step' && stepsOrderMap) {
          const orderA =
            stepsOrderMap.get(a.entityId) ?? Number.MAX_SAFE_INTEGER;
          const orderB =
            stepsOrderMap.get(b.entityId) ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        }

        if (
          a.entity === 'finalStep' &&
          b.entity === 'finalStep' &&
          finalStepsOrderMap
        ) {
          const orderA =
            finalStepsOrderMap.get(a.entityId) ?? Number.MAX_SAFE_INTEGER;
          const orderB =
            finalStepsOrderMap.get(b.entityId) ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        }

        return 0;
      });
    },
  });

  // Function to update form data in session
  const updateFormData = useCallback(
    async (formData: unknown): Promise<void> => {
      const { sessionDocPath, agentSessionData } = baseSession;

      if (!sessionDocPath || !agentSessionData) {
        return;
      }

      const sessionDocRef = doc(db, sessionDocPath);

      await setDoc(sessionDocRef, { formData }, { mergeFields: ['formData'] });
    },
    [baseSession],
  );

  return {
    agentChanges: baseSession.agentChanges,
    agentHasUnreviewedChanges: baseSession.agentHasUnreviewedChanges,
    agentUpdateChanges: baseSession.agentUpdateChanges,
    agentUpdateFormData: updateFormData,
    agentSessionData: baseSession.agentSessionData,
  };
}
